"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TakeoffLayoutChanged = void 0;
exports.assertFillable = assertFillable;
exports.readFillableTemplate = readFillableTemplate;
// Before the export writes into a take-off it didn't make (Templafy's current
// one), it checks the cells are where takeoff-fill.ts writes them: the header
// labels beside its inputs, the branch list, and 20 internal and 20 external
// area blocks with every row it fills (not the negotiating row: the header's
// N29 drives it, and its label is a formula Templafy's copy holds no value
// for). A moved layout is refused, never written into the wrong cells (#516).
const takeoff_layout_ts_1 = require("./takeoff-layout.js");
const takeoff_xlsx_ts_1 = require("./takeoff-xlsx.js");
/** The label beside each header input, as rev 41 has it (TAKEOFF-LAYOUT.md §2). */
const HEADER_LABELS = {
    A3: 'Client Name',
    A4: 'Client Address',
    A13: 'File Name/Project',
    A20: 'Date',
    A21: 'Estimator',
    A23: 'Job Number',
    // All six paint slots: the export writes each one's label and cost.
    G11: 'Standard Paint',
    G12: 'Two Pack (Light)',
    G13: 'Two Pack (Dark)',
    K11: 'Other Paint',
    K12: 'Other Paint',
    K13: 'Other Paint',
    G16: 'Mark-Up Material',
    G17: 'Mark-Up Other',
    L29: 'Negotiating Factor',
};
/** Each block's surface rows, top to bottom, as rev 41 labels them (TAKEOFF-LAYOUT.md §3): the export places surfaces by them. */
const SURFACE_LABELS = {
    Internal: ['Ceilings', 'Ceilings Detailed', 'Walls', 'Walls Detailed', 'Windows (LM)', 'Windows (M2)', 'Doors', 'Door frames', 'Skirting', 'Other', 'Other', 'Other', 'Other', 'Other'],
    External: ['Gutter', 'Fascia', 'Eaves', 'Soffit', 'Walls - Flat', 'Walls Rough (adjust spread rate)', 'Windows (LM)', 'Windows (M2)', 'Downpipes', 'Doors', 'Balustrades/Handrails', 'Posts', 'Other', 'Other', 'Other'],
};
const BLOCKS_PER_SIDE = 20;
const OTHER_TRADE_ROWS = 7;
const BRANCH_LIST_HEAD = { address: 'A63', text: 'Please Select Branch' };
class TakeoffLayoutChanged extends Error {
}
exports.TakeoffLayoutChanged = TakeoffLayoutChanged;
const LAYOUT_CHANGED = "The Templafy take-off has changed, so it can't be filled safely; tell Business Systems.";
const squash = (text) => text.replace(/[\s_:]+/g, '').toLowerCase();
/** Throws TakeoffLayoutChanged, naming what moved, unless the template is the layout the export knows. */
function assertFillable(template, layout) {
    const problems = [];
    for (const [address, label] of Object.entries(HEADER_LABELS)) {
        if (!squash(template.text(takeoff_layout_ts_1.SHEET.takeOff, address)).startsWith(squash(label)))
            problems.push(`${address} isn't "${label}"`);
    }
    if (!squash(template.text('Formulas', BRANCH_LIST_HEAD.address)).includes(squash(BRANCH_LIST_HEAD.text)))
        problems.push('the branch list has moved');
    for (const side of ['Internal', 'External']) {
        const blocks = layout.blocks.filter((b) => b.side === side);
        if (blocks.length !== BLOCKS_PER_SIDE)
            problems.push(`${blocks.length} ${side.toLowerCase()} area blocks, not ${BLOCKS_PER_SIDE}`);
    }
    for (const b of layout.blocks) {
        const labels = b.surfaces.map((r) => squash(template.text(takeoff_layout_ts_1.SHEET.takeOff, `A${r}`)));
        if (labels.join('|') !== SURFACE_LABELS[b.side].map(squash).join('|'))
            problems.push(`area block ${b.number} (row ${b.header})'s surface rows aren't rev 41's`);
        const missing = [
            !b.surfaces.length && 'surface rows',
            !b.sundries.length && 'a sundry row',
            b.wash === null && 'a wash row',
            b.scaffold === null && 'a scaffold row',
            b.consumables === null && 'a consumables row',
            !b.hire.some((h) => h.cartage.length && h.fuel !== null) && 'a hire item',
            b.fuelLevy === null && 'a fuel levy row',
            b.otherTrades.length !== OTHER_TRADE_ROWS && `${OTHER_TRADE_ROWS} other-trade rows`,
            b.overtime === null && 'an overtime row',
            b.foreman === null && 'a foreman row',
            b.abseil === null && 'an abseil row',
        ].filter(Boolean);
        if (missing.length)
            problems.push(`area block ${b.number} (row ${b.header}) is missing ${missing.join(', ')}`);
    }
    if (problems.length) {
        throw new TakeoffLayoutChanged(`${LAYOUT_CHANGED} ${problems.slice(0, 5).join('; ')}.`);
    }
}
/**
 * Templafy's take-off, read and checked: its values and where its blocks are.
 * Anything short of the layout the export knows — a sheet missing or renamed,
 * no area blocks to find, a moved row — is TakeoffLayoutChanged, never a crash.
 */
function readFillableTemplate(parts) {
    try {
        const values = new takeoff_xlsx_ts_1.WorkbookCells(parts);
        const layout = (0, takeoff_layout_ts_1.locate)(values, values.lastRow(takeoff_layout_ts_1.SHEET.takeOff));
        assertFillable(values, layout);
        return { values, layout };
    }
    catch (err) {
        if (err instanceof takeoff_xlsx_ts_1.MissingFromWorkbook || err instanceof takeoff_layout_ts_1.NotATakeoff)
            throw new TakeoffLayoutChanged(`${LAYOUT_CHANGED} ${err.message}`);
        throw err;
    }
}
